import { useEffect } from 'react';
import { PATIENT_PHOTO_FOLDER, PATIENT_PHOTO_MAX_BYTES, PATIENT_PHOTO_MAX_EDGE_PX } from '@his/shared';
import { del, post, put } from '@/lib/api';
import { tr } from '@/lib/i18n-core';

export const PHOTO_QUALITY = 0.85;
export const PHOTO_PICK_TYPES = 'image/jpeg,image/png,image/webp';

export type PhotoDraft = { kind: 'keep' } | { kind: 'new'; blob: Blob; previewUrl: string } | { kind: 'remove' };

export const KEEP_PHOTO: PhotoDraft = { kind: 'keep' };

export type PhotoProblem = 'photo_unreadable' | 'photo_too_large' | 'photo_upload_failed';

export class PhotoError extends Error {
  constructor(readonly problem: PhotoProblem) {
    super(problem);
  }
}

export function useObjectUrlCleanup(url: string | null | undefined): void {
  useEffect(() => {
    if (!url) return undefined;
    return () => URL.revokeObjectURL(url);
  }, [url]);
}

export function draftPreview(draft: PhotoDraft): string | null {
  return draft.kind === 'new' ? draft.previewUrl : null;
}

export function photoProblemText(error: unknown): string {
  if (error instanceof PhotoError) return tr('opd', error.problem);
  return error instanceof Error ? error.message : tr('opd', 'photo_upload_failed');
}

export function fitWithin(width: number, height: number, maxEdge: number = PATIENT_PHOTO_MAX_EDGE_PX): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0 };
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function decodeImage(file: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
  }
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.src = url;
  try {
    await image.decode();
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
  return { source: image, width: image.naturalWidth, height: image.naturalHeight, release: () => URL.revokeObjectURL(url) };
}

function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY));
}

export const CARD_IMAGE_MAX_EDGE_PX = 1600;

export function downscalePhoto(file: Blob): Promise<Blob> {
  return downscaleImage(file, PATIENT_PHOTO_MAX_EDGE_PX);
}

export async function downscaleImage(file: Blob, maxEdge: number): Promise<Blob> {
  const image = await decodeImage(file).catch(() => {
    throw new PhotoError('photo_unreadable');
  });
  try {
    const size = fitWithin(image.width, image.height, maxEdge);
    if (size.width === 0) throw new PhotoError('photo_unreadable');
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    if (!context) throw new PhotoError('photo_unreadable');
    context.drawImage(image.source, 0, 0, size.width, size.height);
    const blob = await canvasBlob(canvas);
    if (!blob) throw new PhotoError('photo_unreadable');
    if (blob.size > PATIENT_PHOTO_MAX_BYTES) throw new PhotoError('photo_too_large');
    return blob;
  } finally {
    image.release();
  }
}

interface PresignedUpload {
  key: string;
  url: string;
  method: string;
  headers?: Record<string, string>;
}

export async function uploadPatientPhoto(patientId: string, photo: Blob): Promise<string> {
  const presigned = await post<PresignedUpload>('/files/presign-upload', {
    folder: PATIENT_PHOTO_FOLDER,
    filename: 'photo.jpg',
    contentType: 'image/jpeg',
    sizeBytes: photo.size,
  });
  const response = await fetch(presigned.url, { method: presigned.method, headers: presigned.headers, body: photo });
  if (!response.ok) throw new PhotoError('photo_upload_failed');
  const saved = await put<{ photoUrl: string }>(`/patients/${patientId}/photo`, { fileKey: presigned.key });
  return saved.photoUrl;
}

export async function applyPhotoDraft(patientId: string, draft: PhotoDraft): Promise<void> {
  if (draft.kind === 'new') await uploadPatientPhoto(patientId, draft.blob);
  else if (draft.kind === 'remove') await del(`/patients/${patientId}/photo`);
}
